import { useState, useEffect, useMemo } from 'react';
import styles from '../../styles/ModuleComparisonChart.module.css';
import { 
  Paper, 
  Typography, 
  Box, 
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Autocomplete,
  TextField
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Label,
  Area,
  ComposedChart
} from 'recharts';

// Color palette for module lines
const COLORS = [
  '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8',
  '#82ca9d', '#ffc658', '#8dd1e1', '#a4de6c', '#d0ed57'
];

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
// eslint-disable-next-line no-unused-vars
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

// Custom tooltip component that shows the exact value when hovering data points
const CustomTooltip = ({ active, payload, label, chartType }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ 
        backgroundColor: '#fff', 
        border: '1px solid #ccc',
        padding: '10px',
        borderRadius: '5px',
        boxShadow: '0px 2px 5px rgba(0,0,0,0.1)'
      }}>
        <p style={{ margin: '0', fontWeight: 'bold' }}>{`Period: ${label}`}</p>
        {payload.map((entry, index) => (
          <p key={`item-${index}`} style={{ margin: '0', color: entry.color }}>
            {chartType === 'combined' && entry.dataKey === 'total' 
              ? `Total Activities: ${entry.value}` 
              : `${entry.name}: ${entry.value} activities`}
          </p>
        ))}
      </div>
    );
  }

  return null;
};

// Helper function to get text color based on background color
// eslint-disable-next-line no-unused-vars
function getContrastColor(hexColor) {
  // Convert hex to RGB
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  
  // Calc luminance - simplified formula
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  // Return white or black based on luminance
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
}

// Custom Legend component that handles interactivity
const CustomizedLegend = (props) => {
  const { payload, activeModule, onModuleClick } = props;
  
  if (!payload || payload.length === 0) return null;

  // If there's only one item and it's "total", don't render the legend
  if (payload.length === 1 && payload[0].value === 'total') {
    return null;
  }
  
  return (
    <ul style={{ 
      display: 'flex', 
      flexWrap: 'wrap', 
      justifyContent: 'center', 
      listStyle: 'none', 
      padding: 0, 
      margin: '10px 0 0' 
    }}>
      {payload.map((entry, index) => {
        const isActive = activeModule === entry.value;
        
        return (
          <li 
            key={`legend-item-${index}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              marginRight: 20,
              marginBottom: 10,
              cursor: 'pointer',
              color: '#000000',
              transition: 'all 0.2s ease',
              backgroundColor: isActive ? '#f0f0f0' : 'transparent',
              padding: '3px 8px',
              borderRadius: '4px',
              opacity: activeModule && !isActive ? 0.5 : 1
            }}
            onClick={() => onModuleClick(entry.value)}
          >
            <span
              style={{
                display: 'inline-block',
                width: 14,
                height: 14,
                marginRight: 5,
                backgroundColor: entry.color,
                borderRadius: '50%'
              }}
            />
            {entry.value}
          </li>
        );
      })}
    </ul>
  );
};

const ModuleComparisonChart = ({ data, granularity = 'weekly' }) => {
  const [timeGranularity, setTimeGranularity] = useState(granularity);
  const [selectedModules, setSelectedModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeModule, setActiveModule] = useState(null);
  const [chartType, setChartType] = useState('combined'); // Set combined as default
  
  // Extract all available modules
  const allModules = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    const modules = new Set();
    data.forEach(entry => {
      if (!entry.modules) return;
      
      entry.modules.forEach(module => {
        if (module && module.module) {
          modules.add(module.module);
        }
      });
    });
    
    return Array.from(modules).sort();
  }, [data]);
  
  // Initialize with up to 9 modules by default
  useEffect(() => {
    if (allModules.length > 0 && selectedModules.length === 0) {
      // Take up to 9 modules from all available modules
      const initialSelection = allModules.slice(0, Math.min(9, allModules.length));
      setSelectedModules(initialSelection);
    }
  }, [allModules, selectedModules.length]);

  // Handle module selection changes
  const handleModuleChange = (event, newValue) => {
    // Limit to max 9 modules
    if (newValue.length <= 9) {
      setSelectedModules(newValue);
    }
  };

  // Handle time granularity changes
  const handleGranularityChange = (event) => {
    setTimeGranularity(event.target.value);
    setLoading(true);
    
    // Simulate loading for UI feedback
    setTimeout(() => setLoading(false), 300);
  };

  // Handle module click in legend
  const handleModuleClick = (module) => {
    // If clicking the same module, toggle off
    if (activeModule === module) {
      setActiveModule(null);
    } else {
      setActiveModule(module);
    }
  };

  // Handle chart type toggle
  const handleChartTypeChange = (event, newChartType) => {
    if (newChartType !== null) {
      setChartType(newChartType);
    }
  };

  // Generate chart data based on selected modules and time granularity
  const chartData = useMemo(() => {
    if (!data || data.length === 0 || selectedModules.length === 0) return [];
    
    let filteredData = [...data];
    
    // Filter based on selected time granularity
    if (timeGranularity === 'weekly') {
      filteredData = filteredData.filter(entry => entry._id && entry._id.group && entry._id.group.includes('W'));
    } else if (timeGranularity === 'monthly') {
      filteredData = filteredData.filter(entry => entry._id && entry._id.group && entry._id.group.includes('M'));
    } else {
      // For daily view, filter out weekly and monthly entries but include ALL days
      filteredData = filteredData.filter(entry => 
        entry._id && entry._id.group && 
        !entry._id.group.includes('W') && 
        !entry._id.group.includes('M')
      );
      
      // No additional filtering based on date range - show all available days
    }
    
    // Sort entries chronologically regardless of year
    filteredData.sort((a, b) => {
      const groupA = a._id.group;
      const groupB = b._id.group;

      if (timeGranularity === 'weekly') {
        // Extract year and week information for proper date sorting
        const [yearA, weekA] = groupA.split('-W').map(Number);
        const [yearB, weekB] = groupB.split('-W').map(Number);
        
        // Create comparable dates - Jan 1 + (week * 7 days)
        const dateA = new Date(yearA, 0, 1 + (weekA - 1) * 7);
        const dateB = new Date(yearB, 0, 1 + (weekB - 1) * 7);
        
        return dateA - dateB; // Chronological order
      } 
      else if (timeGranularity === 'monthly') {
        // Extract year and month information
        const [yearA, monthA] = groupA.split('-M').map(Number);
        const [yearB, monthB] = groupB.split('-M').map(Number);
        
        // Create comparable dates
        const dateA = new Date(yearA, monthA - 1, 1);
        const dateB = new Date(yearB, monthB - 1, 1);
        
        return dateA - dateB; // Chronological order
      }
      else {
        // For daily view, simply compare the date strings chronologically
        return new Date(groupA) - new Date(groupB);
      }
    });
    
    // Create the chart data
    return filteredData.map(entry => {
      // Format period strings for better display
      const period = entry._id.group;
      let formattedPeriod;
      
      if (period.includes('W')) {
        const [year, week] = period.split('-W');
        formattedPeriod = `W${week} ${year}`;
      } else if (period.includes('M')) {
        const [year, month] = period.split('-M');
        const monthName = new Date(year, month - 1, 1).toLocaleString('default', { month: 'short' });
        formattedPeriod = `${monthName} ${year}`;
      } else {
        // Format YYYY-MM-DD as MM/DD for cleaner display in daily view
        const date = new Date(period);
        formattedPeriod = date.toLocaleDateString('en-US', { 
          month: 'short',
          day: 'numeric'
        });
      }
      
      const result = { 
        date: period,
        formattedPeriod
      };
      
      // Only include selected modules and ensure data exists
      if (entry.modules) {
        entry.modules
          .filter(module => module && module.module && selectedModules.includes(module.module))
          .forEach(module => {
            result[module.module] = module.count || 0;
          });
      }
      
      // Calculate total activities for combined view
      if (chartType === 'combined') {
        result.total = entry.modules
          .filter(module => module && module.module && selectedModules.includes(module.module))
          .reduce((sum, module) => sum + (module.count || 0), 0);
      }
        
      return result;
    });
  }, [data, selectedModules, timeGranularity, chartType]);

  return (
    <Paper elevation={2} sx={{ p: 2, mb: 3, minHeight: 500, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h6">Module Usage Over Time</Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: { xs: 1, sm: 0 } }}>
          <FormControl sx={{ minWidth: 120 }} size="small">
            <InputLabel 
              id="time-granularity-label"
              sx={{ '&.Mui-focused': { color: '#1D9999' } }}
            >
              Time View
            </InputLabel>
            <Select
              labelId="time-granularity-label"
              id="time-granularity-select"
              value={timeGranularity}
              label="Time View"
              onChange={handleGranularityChange}
              sx={{ 
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1D9999',
                }
              }}
            >
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
            </Select>
          </FormControl>
          <ToggleButtonGroup
            value={chartType}
            exclusive
            onChange={handleChartTypeChange}
            aria-label="chart type"
            size="small"
            sx={{ 
              '& .Mui-selected': {
                color: '#1D9999 !important',
                backgroundColor: 'rgba(40, 187, 187, 0.08) !important'
              }
            }}
          >
            <ToggleButton 
              value="combined" 
              aria-label="combined"
              className={chartType === 'combined' ? styles['selected-button'] : ''}
            >
              Combined
            </ToggleButton>
            <ToggleButton 
              value="individual" 
              aria-label="individual"
              className={chartType === 'individual' ? styles['selected-button'] : ''}
            >
              Individual
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>
      
      <Box sx={{ mb: 2 }}>
        <Autocomplete
          multiple
          id="module-selection"
          options={allModules}
          value={selectedModules}
          onChange={handleModuleChange}
          renderInput={(params) => (
            <TextField 
              {...params} 
              variant="outlined" 
              label="Compare Modules (max 9)" 
              placeholder={selectedModules.length === 0 ? "Select up to 9 modules to compare" : ""}
              fullWidth
              size="small"
              sx={{
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1D9999',
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#1D9999',
                }
              }}
            />
          )}
          renderTags={(selected, getTagProps) =>
            selected.map((option, index) => (
              <Chip 
                label={option} 
                {...getTagProps({ index })} 
                size="small"
                style={{
                  backgroundColor: COLORS[index % COLORS.length],
                  color: '#fff', // Consistent text color for all chips
                  fontWeight: 600,
                  textShadow: '0px 1px 2px rgba(0,0,0,0.5)', // Text shadow for engraved effect
                  boxShadow: 'inset 0px 1px 3px rgba(0,0,0,0.3)', // Inner shadow for engraved effect
                  border: '1px solid rgba(255,255,255,0.2)' // Subtle border for depth
                }}
              />
            ))
          }
          size="small"
          disableCloseOnSelect
          limitTags={9}
        />
        {selectedModules.length === 0 && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
            Please select up to 9 modules to compare
          </Typography>
        )}
        {selectedModules.length === 9 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Maximum 9 modules can be compared at once
          </Typography>
        )}
      </Box>
      
      <Box sx={{ height: 400, position: 'relative' }}>
        {loading && (
          <Box sx={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            width: '100%', 
            height: '100%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 10
          }}>
            <CircularProgress />
          </Box>
        )}
        
        {!loading && selectedModules.length === 0 && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '100%',
            flexDirection: 'column',
            color: 'text.secondary'
          }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Select modules to compare their usage over time
            </Typography>
            <Typography variant="body2">
              You can compare up to 9 modules at once
            </Typography>
          </Box>
        )}
        
        {!loading && selectedModules.length > 0 && chartData.length === 0 && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '100%',
            color: 'text.secondary'  
          }}>
            <Typography>No data available for selected criteria</Typography>
          </Box>
        )}
        
        {!loading && selectedModules.length > 0 && chartData.length > 0 && (
          <ResponsiveContainer width="100%" height="100%">
            {chartType === 'individual' ? (
              <LineChart
                data={chartData}
                margin={{
                  top: 20, right: 30, left: 20, bottom: 40,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="formattedPeriod" 
                  height={60}
                  angle={-45}
                  textAnchor="end"
                  tick={{ fontSize: 12 }}
                />
                <YAxis>
                  <Label 
                    value="Number of Activities" 
                    angle={-90} 
                    position="insideLeft" 
                    style={{ textAnchor: 'middle' }}
                  />
                </YAxis>
                <Tooltip content={<CustomTooltip chartType={chartType} />} />
                <Legend 
                  content={props => (
                    <CustomizedLegend 
                      {...props}
                      activeModule={activeModule}
                      onModuleClick={handleModuleClick}
                    />
                  )}
                />
                
                {selectedModules.map((module, index) => {
                  const isActive = activeModule === module;
                  const color = COLORS[index % COLORS.length];
                  
                  return (
                    <Line
                      key={module}
                      type="monotone"
                      dataKey={module}
                      name={module}
                      stroke={color}
                      strokeWidth={isActive ? 3 : 2}
                      activeDot={{ r: 8, fill: color }}
                      dot={{ strokeWidth: 2 }}
                      opacity={activeModule && !isActive ? 0.3 : 1}
                    />
                  );
                })}
              </LineChart>
            ) : (
              <ComposedChart
                data={chartData}
                margin={{
                  top: 20, right: 30, left: 20, bottom: 40,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="formattedPeriod" 
                  height={60}
                  angle={-45}
                  textAnchor="end"
                  tick={{ fontSize: 12 }}
                />
                <YAxis>
                  <Label 
                    value="Number of Activities" 
                    angle={-90} 
                    position="insideLeft" 
                    style={{ textAnchor: 'middle' }}
                  />
                </YAxis>
                <Tooltip content={<CustomTooltip chartType={chartType} />} />
                <Legend 
                  content={props => (
                    <CustomizedLegend 
                      {...props}
                      activeModule={activeModule}
                      onModuleClick={handleModuleClick}
                    />
                  )}
                />
                <Area
                  type="monotone"
                  dataKey="total"
                  name="Total Activities"
                  fill="#8884d8"
                  stroke="#8884d8"
                  strokeWidth={2}
                  activeDot={{ r: 8, fill: '#8884d8' }}
                  dot={{ strokeWidth: 2 }}
                />
              </ComposedChart>
            )}
          </ResponsiveContainer>
        )}
      </Box>
      
      {!loading && selectedModules.length > 0 && chartData.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
          {chartType === 'individual' ? 'Click on legend items to highlight specific modules' : 'Showing combined activity for all selected modules'}
        </Typography>
      )}
    </Paper>
  );
};

export default ModuleComparisonChart;